/**
 * EffectsPresetService
 * 
 * Manages effects chain presets with user profile sync and local storage fallback.
 * Provides CRUD operations for presets, category filtering, and favorites management.
 */

import { EffectInstance } from './EffectsManager';
import { userProfileService } from '../api/UserProfileService';

/**
 * Instrument category for organizing presets
 */
export type InstrumentCategory = 'Lead' | 'Bass' | 'Pad' | 'Arp' | 'Percussion' | 'SFX' | 'General';

/**
 * Effects chain preset interface
 */
export interface EffectsChainPreset {
  id: string;
  name: string;
  description?: string | undefined;
  category?: InstrumentCategory | undefined;
  effects: EffectInstance[];
  isFavorite: boolean;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * Local storage key for presets
 */
const LOCAL_STORAGE_KEY = '8bitcomposer_effects_presets';

/**
 * Local storage key for migration flag
 */
const MIGRATION_FLAG_KEY = '8bitcomposer_presets_migrated';

/**
 * EffectsPresetService manages effects chain presets
 */
export class EffectsPresetService {
  private localPresets: Map<string, EffectsChainPreset>;
  private profilePresets: Map<string, EffectsChainPreset>;
  private isAuthenticated: boolean;

  constructor() {
    this.localPresets = new Map();
    this.profilePresets = new Map();
    this.isAuthenticated = false;
    
    // Load presets from local storage on initialization
    this.loadFromLocalStorage();
  }

  /**
   * Initialize the service with authentication state
   */
  async initialize(isAuthenticated: boolean): Promise<void> {
    this.isAuthenticated = isAuthenticated;
    
    if (isAuthenticated) {
      try {
        await this.loadPresetsFromProfile();
        
        // Check if we need to migrate local presets
        await this.migrateLocalPresetsToProfile();
      } catch (error) {
        console.error('Failed to load presets from profile:', error);
        // Fall back to local presets
      }
    }
  }

  /**
   * Load presets from user profile
   * Retrieves presets from profile.effectsPresets or profile.metadata.effectsPresets
   * (checks both locations for backward compatibility)
   */
  async loadPresetsFromProfile(): Promise<EffectsChainPreset[]> {
    if (!this.isAuthenticated) {
      console.warn('Cannot load presets from profile: user not authenticated');
      return [];
    }

    try {
      const profile = await userProfileService.getProfile();
      
      // Get presets from profile.effectsPresets or metadata.effectsPresets
      // Check both locations for backward compatibility
      const presets = profile['effectsPresets'] || (profile.metadata?.['effectsPresets'] as any[]) || [];
      
      // Update profile presets map
      this.profilePresets.clear();
      presets.forEach((preset: any) => {
        // Validate and convert preset to proper type
        const validPreset: EffectsChainPreset = {
          id: preset.id,
          name: preset.name,
          description: preset.description,
          category: preset.category as InstrumentCategory | undefined,
          effects: preset.effects.map((effect: any) => ({
            id: effect.id,
            type: effect.type as EffectInstance['type'],
            parameters: effect.parameters,
            bypassed: effect.bypassed,
            slot: effect.slot,
          })),
          isFavorite: preset.isFavorite,
          isDefault: preset.isDefault,
          createdAt: preset.createdAt,
          updatedAt: preset.updatedAt,
        };
        this.profilePresets.set(validPreset.id, validPreset);
      });
      
      console.log(`Loaded ${presets.length} presets from profile`);
      return Array.from(this.profilePresets.values());
    } catch (error) {
      console.error('Failed to load presets from profile:', error);
      throw error;
    }
  }

  /**
   * Save a new preset
   */
  async savePreset(
    preset: Omit<EffectsChainPreset, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<EffectsChainPreset> {
    const now = new Date().toISOString();
    const id = this.generatePresetId();
    
    const newPreset: EffectsChainPreset = {
      ...preset,
      id,
      createdAt: now,
      updatedAt: now,
    };

    if (this.isAuthenticated) {
      // Save to user profile
      try {
        await this.syncPresetToProfile(newPreset);
        this.profilePresets.set(id, newPreset);
      } catch (error) {
        console.error('Failed to save preset to profile:', error);
        // Fall back to local storage
        this.localPresets.set(id, newPreset);
        this.saveToLocalStorage();
      }
    } else {
      // Save to local storage
      this.localPresets.set(id, newPreset);
      this.saveToLocalStorage();
    }

    return newPreset;
  }

  /**
   * Update an existing preset
   */
  async updatePreset(
    id: string,
    updates: Partial<EffectsChainPreset>
  ): Promise<EffectsChainPreset> {
    const existingPreset = this.getPresetById(id);
    
    if (!existingPreset) {
      throw new Error(`Preset with id ${id} not found`);
    }

    const updatedPreset: EffectsChainPreset = {
      ...existingPreset,
      ...updates,
      id, // Ensure id doesn't change
      createdAt: existingPreset.createdAt, // Preserve creation date
      updatedAt: new Date().toISOString(),
    };

    if (this.isAuthenticated && this.profilePresets.has(id)) {
      // Update in user profile
      try {
        await this.syncPresetToProfile(updatedPreset);
        this.profilePresets.set(id, updatedPreset);
      } catch (error) {
        console.error('Failed to update preset in profile:', error);
        throw error;
      }
    } else {
      // Update in local storage
      this.localPresets.set(id, updatedPreset);
      this.saveToLocalStorage();
    }

    return updatedPreset;
  }

  /**
   * Delete a preset
   */
  async deletePreset(id: string): Promise<void> {
    if (this.isAuthenticated && this.profilePresets.has(id)) {
      // Delete from user profile
      try {
        await this.deletePresetFromProfile(id);
        this.profilePresets.delete(id);
      } catch (error) {
        console.error('Failed to delete preset from profile:', error);
        throw error;
      }
    } else if (this.localPresets.has(id)) {
      // Delete from local storage
      this.localPresets.delete(id);
      this.saveToLocalStorage();
    } else {
      throw new Error(`Preset with id ${id} not found`);
    }
  }

  /**
   * Toggle favorite status for a preset
   */
  async toggleFavorite(id: string): Promise<void> {
    const preset = this.getPresetById(id);
    
    if (!preset) {
      throw new Error(`Preset with id ${id} not found`);
    }

    await this.updatePreset(id, {
      isFavorite: !preset.isFavorite,
    });
  }

  /**
   * Get all presets
   */
  getAllPresets(): EffectsChainPreset[] {
    const allPresets = new Map<string, EffectsChainPreset>();
    
    // Add local presets
    this.localPresets.forEach((preset, id) => {
      allPresets.set(id, preset);
    });
    
    // Add profile presets (overwrite local if same id)
    this.profilePresets.forEach((preset, id) => {
      allPresets.set(id, preset);
    });
    
    return Array.from(allPresets.values()).sort((a, b) => {
      // Sort by creation date, newest first
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }

  /**
   * Get preset by ID
   */
  getPresetById(id: string): EffectsChainPreset | undefined {
    return this.profilePresets.get(id) || this.localPresets.get(id);
  }

  /**
   * Get presets by category
   */
  getPresetsByCategory(category: InstrumentCategory): EffectsChainPreset[] {
    return this.getAllPresets().filter(preset => preset.category === category);
  }

  /**
   * Get favorite presets
   */
  getFavoritePresets(): EffectsChainPreset[] {
    return this.getAllPresets().filter(preset => preset.isFavorite);
  }

  /**
   * Get default presets
   */
  getDefaultPresets(): EffectsChainPreset[] {
    return this.getAllPresets().filter(preset => preset.isDefault);
  }

  /**
   * Duplicate a preset
   */
  async duplicatePreset(id: string): Promise<EffectsChainPreset> {
    const preset = this.getPresetById(id);
    
    if (!preset) {
      throw new Error(`Preset with id ${id} not found`);
    }

    const duplicateData: Omit<EffectsChainPreset, 'id' | 'createdAt' | 'updatedAt'> = {
      name: `${preset.name} (Copy)`,
      effects: preset.effects.map(effect => ({ ...effect })),
      isFavorite: false,
      isDefault: false,
    };

    // Only add optional fields if they exist
    if (preset.description !== undefined) {
      duplicateData.description = preset.description;
    }
    if (preset.category !== undefined) {
      duplicateData.category = preset.category;
    }

    return this.savePreset(duplicateData);
  }

  /**
   * Migrate local presets to user profile
   * Keeps local storage as fallback in case of API errors
   */
  async migrateLocalPresetsToProfile(): Promise<void> {
    if (!this.isAuthenticated) {
      console.warn('Cannot migrate presets: user not authenticated');
      return;
    }

    // Check if migration has already been done
    const migrationFlag = localStorage.getItem(MIGRATION_FLAG_KEY);
    if (migrationFlag === 'true') {
      console.log('Presets already migrated');
      return;
    }

    // Get local presets
    const localPresets = Array.from(this.localPresets.values());
    
    if (localPresets.length === 0) {
      console.log('No local presets to migrate');
      localStorage.setItem(MIGRATION_FLAG_KEY, 'true');
      return;
    }

    console.log(`Migrating ${localPresets.length} local presets to profile...`);

    try {
      // Sync all local presets to profile
      for (const preset of localPresets) {
        await this.syncPresetToProfile(preset);
        this.profilePresets.set(preset.id, preset);
      }

      // Keep local storage as fallback (don't clear it)
      // This ensures we have a backup if API calls fail in the future
      localStorage.setItem(MIGRATION_FLAG_KEY, 'true');

      console.log('Successfully migrated local presets to profile (local storage kept as fallback)');
    } catch (error) {
      console.error('Failed to migrate presets:', error);
      throw error;
    }
  }

  /**
   * Sync a preset to user profile
   * Stores presets in the profile's metadata.effectsPresets field
   * (UserProfileService only allows updating metadata and preferences)
   */
  private async syncPresetToProfile(preset: EffectsChainPreset): Promise<void> {
    try {
      // Get current profile
      const profile = await userProfileService.getProfile();
      
      // Get effectsPresets array from profile.effectsPresets or metadata
      const presets = profile['effectsPresets'] || (profile.metadata?.['effectsPresets'] as any[]) || [];
      const existingIndex = presets.findIndex((p: any) => p.id === preset.id);
      
      // Convert preset to profile format
      const profilePreset = this.convertToProfileFormat(preset);
      
      // Create updated presets array
      const updatedPresets = [...presets];
      if (existingIndex >= 0) {
        // Update existing preset
        updatedPresets[existingIndex] = profilePreset;
      } else {
        // Add new preset
        updatedPresets.push(profilePreset);
      }

      // Update profile with new presets in metadata
      // Since UserProfileService only allows updating metadata and preferences,
      // we store effectsPresets in metadata
      await userProfileService.updateProfile({
        metadata: {
          ...profile.metadata,
          effectsPresets: updatedPresets,
        },
      });
    } catch (error) {
      console.error('Failed to sync preset to profile:', error);
      throw error;
    }
  }

  /**
   * Convert EffectsChainPreset to profile format
   */
  private convertToProfileFormat(preset: EffectsChainPreset): any {
    return {
      id: preset.id,
      name: preset.name,
      description: preset.description,
      category: preset.category,
      effects: preset.effects.map(effect => ({
        id: effect.id,
        type: effect.type,
        parameters: effect.parameters,
        bypassed: effect.bypassed,
        slot: effect.slot,
      })),
      isFavorite: preset.isFavorite,
      isDefault: preset.isDefault,
      createdAt: preset.createdAt,
      updatedAt: preset.updatedAt,
    };
  }

  /**
   * Delete a preset from user profile
   */
  private async deletePresetFromProfile(id: string): Promise<void> {
    try {
      // Get current profile
      const profile = await userProfileService.getProfile();
      
      // Get effectsPresets from profile.effectsPresets or metadata
      const presets = profile['effectsPresets'] || (profile.metadata?.['effectsPresets'] as any[]) || [];
      
      // Filter out the preset
      const updatedPresets = presets.filter((p: any) => p.id !== id);

      // Update profile with filtered presets in metadata
      await userProfileService.updateProfile({
        metadata: {
          ...profile.metadata,
          effectsPresets: updatedPresets,
        },
      });
    } catch (error) {
      console.error('Failed to delete preset from profile:', error);
      throw error;
    }
  }

  /**
   * Save presets to local storage
   */
  private saveToLocalStorage(): void {
    try {
      const presets = Array.from(this.localPresets.values());
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(presets));
    } catch (error) {
      console.error('Failed to save presets to local storage:', error);
    }
  }

  /**
   * Load presets from local storage
   */
  private loadFromLocalStorage(): void {
    try {
      const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
      
      if (stored) {
        const presets: EffectsChainPreset[] = JSON.parse(stored);
        this.localPresets.clear();
        
        presets.forEach(preset => {
          this.localPresets.set(preset.id, preset);
        });
        
        console.log(`Loaded ${presets.length} presets from local storage`);
      }
    } catch (error) {
      console.error('Failed to load presets from local storage:', error);
    }
  }

  /**
   * Generate a unique preset ID
   */
  private generatePresetId(): string {
    return `preset-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
  }

  /**
   * Clear all presets (for testing/debugging)
   */
  clearAllPresets(): void {
    this.localPresets.clear();
    this.profilePresets.clear();
    localStorage.removeItem(LOCAL_STORAGE_KEY);
    localStorage.removeItem(MIGRATION_FLAG_KEY);
  }
}

// Export singleton instance
export const effectsPresetService = new EffectsPresetService();
